import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useCampaign } from "@/contexts/CampaignContext";
import * as Sentry from "@sentry/react";

export default function Settings() {
  console.log('Settings component rendered');
  const { selectedCampaignId } = useCampaign();

  const [sendTime, setSendTime] = useState("11:00");
  const [status, setStatus] = useState<"active" | "paused" | "completed">("active");
  const [totalDays, setTotalDays] = useState(21);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: campaign, isLoading } = useQuery({
    queryKey: ['campaign-settings', selectedCampaignId],
    enabled: !!selectedCampaignId,
    queryFn: async () => {
      if (!selectedCampaignId) return null;

      const { data, error } = await supabase
        .from("campaigns")
        .select("*")
        .eq("id", selectedCampaignId)
        .single();

      if (error) {
        console.error("Error loading campaign:", error);
        throw error;
      }

      return data;
    },
  });

  useEffect(() => {
    if (campaign) {
      console.log("Setting campaign values:", {
        send_time: campaign.send_time,
        status: campaign.status,
        total_days: campaign.total_days,
        current_campaign_day: campaign.current_campaign_day
      });
      setSendTime(campaign.send_time || "11:00");
      setStatus(campaign.status || "active");
      setTotalDays(campaign.total_days);
    }
  }, [campaign]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!selectedCampaignId) {
        throw new Error("No campaign selected");
      }

      const { error } = await supabase
        .from("campaigns")
        .update({
          send_time: sendTime,
          status: status,
          total_days: totalDays,
        })
        .eq("id", selectedCampaignId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Campaign settings saved successfully",
      });
      // Invalidate all related caches
      queryClient.invalidateQueries({ queryKey: ['campaign-settings'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-days'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to save campaign settings",
        variant: "destructive",
      });
    },
  });


  const changePasswordMutation = useMutation({
    mutationFn: async ({ oldPassword, newPassword }: { oldPassword: string; newPassword: string }) => {
      const { data, error } = await supabase.functions.invoke('change-admin-password', {
        body: { oldPassword, newPassword }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Admin PIN changed successfully",
      });
      setOldPassword("");
      setNewPassword("");
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to change PIN",
        variant: "destructive",
      });
    },
  });

  const handleSave = () => {
    saveMutation.mutate();
  };

  const handleChangePassword = () => {
    if (!oldPassword || !newPassword) {
      toast({
        title: "Error",
        description: "Please enter both current and new PIN",
        variant: "destructive",
      });
      return;
    }

    if (newPassword.length !== 4 || !/^\d{4}$/.test(newPassword)) {
      toast({
        title: "Error",
        description: "New PIN must be exactly 4 digits",
        variant: "destructive",
      });
      return;
    }

    changePasswordMutation.mutate({ oldPassword, newPassword });
  };

  const handleTestSentry = () => {
    toast({
      title: "Triggering Test Error",
      description: "Check your Sentry dashboard in a moment...",
    });

    // Trigger a test error
    setTimeout(() => {
      throw new Error("Test error from admin settings - Sentry is working!");
    }, 100);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl md:text-3xl font-bold">Settings</h1>

      <Card>
        <CardHeader>
          <CardTitle>Email Campaign Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="sendTime">Send Time (Hour)</Label>
            <Select value={sendTime} onValueChange={setSendTime} disabled>
              <SelectTrigger disabled>
                <SelectValue placeholder="Select hour" />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 24 }, (_, i) => {
                  const hour = i.toString().padStart(2, '0');
                  const displayTime = i === 0 ? '12:00 AM' :
                                   i < 12 ? `${i}:00 AM` :
                                   i === 12 ? '12:00 PM' :
                                   `${i - 12}:00 PM`;
                  return (
                    <SelectItem key={hour} value={`${hour}:00`}>
                      {displayTime}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Send time is currently controlled via cron-jobs.org
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="totalDays">Total Campaign Days</Label>
            <Input
              id="totalDays"
              type="number"
              min="1"
              value={totalDays}
              onChange={(e) => setTotalDays(parseInt(e.target.value))}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="status">Campaign Status</Label>
            <Select value={status} onValueChange={(value: any) => setStatus(value)}>
              <SelectTrigger id="status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Active campaigns send emails daily. Paused and completed campaigns do not send.
            </p>
          </div>

          <Button onClick={handleSave} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? "Saving..." : "Save Settings"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Admin PIN</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="oldPassword">Current PIN</Label>
            <Input
              id="oldPassword"
              type="password"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              maxLength={4}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="newPassword">New PIN</Label>
            <Input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="4 digits"
              maxLength={4}
            />
          </div>

          <Button onClick={handleChangePassword} disabled={changePasswordMutation.isPending}>
            {changePasswordMutation.isPending ? "Changing..." : "Change PIN"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Sentry Error Monitoring</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Test that error reporting is working correctly. This will trigger a test error and send it to Sentry.
          </p>
          <Button onClick={handleTestSentry} variant="outline">
            Trigger Test Error
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
